import { useToastController } from '@mythical/ui'
import useMutation from 'app/hooks/use-mutation'
import {
  unlockContentMutation,
  unlockResult,
} from 'app/mutations/content/unlock'
import { observer } from 'mobx-react-lite'
import React from 'react'
import { ChevronRight, LockKeyhole } from '@tamagui/lucide-icons'
import { Button, ButtonProps } from '../primitive/Button'
import Spinner from '../primitive/Spinner'

const query = unlockContentMutation()

interface UnlockButtonProps {
  id: string
  onUnlock: () => void
  buttonProps?: ButtonProps
  variant?: 'default' | 'secondary'
}

export const UnlockButton = observer(
  ({ id, onUnlock, buttonProps, variant = 'default' }: UnlockButtonProps) => {
    const toast = useToastController()
    const { loading, data, makeRequest } = useMutation(query, unlockResult)

    const handleUnlock = async () => {
      const data = await makeRequest({ id })
      if (data?.success) {
        onUnlock()
      } else {
        toast.show('Failed to unlock', {
          message: data?.reason || 'An unknown error occurred',
          duration: 2000,
          customData: {
            preset: 'error',
          },
        })
      }
    }

    if (variant === 'secondary') {
      return (
        <Button
          theme="secondary8Outline"
          fontFamily="$heading"
          fontWeight="500"
          onPress={handleUnlock}
          scaleIcon={1.2}
          scaleSpace={0.3}
          icon={
            loading && !data ? (
              <Spinner scale={0.65} secondary />
            ) : (
              <LockKeyhole color="$secondary4" y={-1} />
            )
          }
          iconAfter={<ChevronRight color="$secondary1" />}
          {...buttonProps}
        >
          {!data?.success && 'Un-Lock Video'}

          {data && data.success && 'Unlocked'}
        </Button>
      )
    }

    return (
      <Button onPress={handleUnlock} {...buttonProps}>
        {loading && !data && <Spinner secondary />}
        {!loading && !data?.success && 'Unlock'}
        {data && data.success && 'Unlocked'}
      </Button>
    )
  }
)
